import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { I18nProvider } from "@/contexts/i18nContext";

// Lazy-load non-critical UI chrome to reduce TTI
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const SimulationBanner = lazy(() => import("@/components/SimulationBanner"));
const BottomNav = lazy(() => import("@/components/BottomNav"));

// Lazy load all pages for code splitting
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const GodModeDashboard = lazy(() => import("./pages/GodModeDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const DemiUserDashboard = lazy(() => import("./pages/DemiUserDashboard"));
const SovereignDashboard = lazy(() => import("./pages/SovereignDashboard"));
const ServiceMarketplace = lazy(() => import("./pages/ServiceMarketplace"));
const MapPage = lazy(() => import("./pages/MapPage"));
const ConciergePage = lazy(() => import("./pages/ConciergePage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const GuestPassPage = lazy(() => import("./pages/GuestPassPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const BookingHistoryPage = lazy(() => import("./pages/BookingHistoryPage"));
const RentalManagementPage = lazy(() => import("./pages/RentalManagementPage"));
const PublicRentalsPage = lazy(() => import("./pages/PublicRentalsPage"));
const BrowseRentalsPage = lazy(() => import("./pages/BrowseRentalsPage"));
const PublicSalesPage = lazy(() => import("./pages/PublicSalesPage"));
const SaleDetailPage = lazy(() => import("./pages/SaleDetailPage"));
const BookingConfirmationPage = lazy(() => import("./pages/BookingConfirmationPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const SmartHomeGuidePage = lazy(() => import("./pages/SmartHomeGuidePage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PropertyDirectionsPage = lazy(() => import("./pages/PropertyDirectionsPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const ListingDetailPage = lazy(() => import("./pages/ListingDetailPage"));
const PaymentHistoryPage = lazy(() => import("./pages/PaymentHistoryPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const AIPage = lazy(() => import("./pages/AIPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TenantProvider>
      <I18nProvider>
        <AuthProvider>
          <TooltipProvider>
            <Suspense fallback={null}>
              <Toaster />
              <Sonner position="top-center" />
            </Suspense>
            <BrowserRouter>
              <Suspense fallback={null}><SimulationBanner /></Suspense>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/god-mode" element={<GodModeDashboard />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/sovereign" element={<SovereignDashboard />} />
                  <Route path="/guest" element={<DemiUserDashboard />} />
                  <Route path="/services" element={<ServiceMarketplace />} />
                  <Route path="/map" element={<MapPage />} />
                  <Route path="/concierge" element={<ConciergePage />} />
                  <Route path="/wallet" element={<WalletPage />} />
                  <Route path="/ai" element={<AIPage />} />
                  <Route path="/guest-passes" element={<GuestPassPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/bookings" element={<BookingHistoryPage />} />
                  <Route path="/rentals" element={<RentalManagementPage />} />
                  <Route path="/public-rentals" element={<PublicRentalsPage />} />
                  <Route path="/browse-rentals" element={<BrowseRentalsPage />} />
                  <Route path="/listing/:listingId" element={<ListingDetailPage />} />
                  <Route path="/properties-for-sale" element={<PublicSalesPage />} />
                  <Route path="/sale/:listingId" element={<SaleDetailPage />} />
                  <Route path="/booking-confirmation" element={<BookingConfirmationPage />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/payment-history" element={<PaymentHistoryPage />} />
                  <Route path="/smart-home-guide" element={<SmartHomeGuidePage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/property-directions" element={<PropertyDirectionsPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <Suspense fallback={null}><BottomNav /></Suspense>
              <Suspense fallback={null}><CookieConsent /></Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </I18nProvider>
    </TenantProvider>
  </QueryClientProvider>
);

export default App;
